
import React from "react";
import { Clock, CheckCircle, XCircle, AlertCircle, CalendarDays } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useSubscriptionsStats } from "@/hooks/useSubscriptionsStats";

export const SubscriptionStats = () => {
  const { 
    activeProducts, 
    expiredProducts, 
    unpaidProducts, 
    nextBill,
    loading,
  } = useSubscriptionsStats();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="bg-gray-50 animate-pulse h-32"></Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      <StatCard 
        title="Active Products"
        value={activeProducts.length}
        icon={<CheckCircle className="h-8 w-8 text-green-500" />}
        description={activeProducts.length === 0 ? 
          "No active products" : 
          activeProducts.length === 1 ? 
            `${activeProducts[0]}` : 
            `${activeProducts[0]} and ${activeProducts.length - 1} more`
        }
        color="bg-green-50 border-green-100"
      />
      
      <StatCard 
        title="Expired Products"
        value={expiredProducts.length}
        icon={<XCircle className="h-8 w-8 text-gray-500" />}
        description={expiredProducts.length === 0 ? 
          "No expired products" : 
          expiredProducts.length === 1 ? 
            `${expiredProducts[0]}` : 
            `${expiredProducts[0]} and ${expiredProducts.length - 1} more`
        }
        color="bg-gray-50 border-gray-100"
      />
      
      <StatCard 
        title="Unpaid Products"
        value={unpaidProducts.length}
        icon={<AlertCircle className="h-8 w-8 text-yellow-500" />}
        description={unpaidProducts.length === 0 ? 
          "No unpaid products" : 
          unpaidProducts.length === 1 ? 
            `${unpaidProducts[0]}` : 
            `${unpaidProducts[0]} and ${unpaidProducts.length - 1} more`
        }
        color="bg-yellow-50 border-yellow-100"
      />
      
      <StatCard 
        title="Next Bill Due"
        value={nextBill ? new Date(nextBill.due_date).toLocaleDateString() : "None"}
        icon={<CalendarDays className="h-8 w-8 text-blue-500" />}
        description={nextBill ? 
          `${nextBill.product_name} - ${nextBill.plan_name}` : 
          "No upcoming bills"
        }
        color="bg-blue-50 border-blue-100"
      />
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

const StatCard = ({ title, value, icon, description, color }: StatCardProps) => {
  return (
    <Card className={`border ${color} shadow-sm hover:shadow transition-shadow`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-500">{title}</h3>
            <p className="text-2xl font-bold mt-1">{value}</p>
            <p className="text-xs text-gray-500 mt-1 truncate max-w-[180px]" title={description}>
              {description}
            </p>
          </div>
          <div>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
